import React, { useState, useEffect } from 'react';
import { Bell, ArrowUpRight, AtSign, Gavel, Handshake, Copy, Check } from 'lucide-react';
import { GramIcon } from '@/components/ui/GramIcon';
import { UserTypeIcon, getUserKind, type UserKind } from '@/components/ui/UserTypeIcon';
import { useTranslation } from 'react-i18next';

const DARK_PALETTES = [
    { bg: '#174d29', textColor: '#ffffff' },
    { bg: '#1c1d22', textColor: '#fef08a' },
    { bg: '#0f243f', textColor: '#e0f2fe' },
    { bg: '#271138', textColor: '#fae8ff' },
    { bg: '#25282e', textColor: '#f1f5f9' },
];

const NavStoreIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 20" className={className} style={{ fill: 'currentColor' }}>
        <path d="M16.8035 6.96427C16.1335 6.22427 15.1235 5.79427 13.7235 5.64427V4.88427C13.7235 3.51427 13.1435 2.19427 12.1235 1.27427C11.6202 0.812823 11.025 0.462927 10.3771 0.247511C9.72909 0.032095 9.04292 -0.0439787 8.3635 0.0242742C5.9735 0.254274 3.9635 2.56427 3.9635 5.06427V5.64427C2.5635 5.79427 1.5535 6.22427 0.883496 6.96427C-0.0865043 8.04427 -0.0565042 9.48427 0.0534958 10.4843L0.753496 16.0543C0.963496 18.0043 1.7535 20.0043 6.0535 20.0043H11.6335C15.9335 20.0043 16.7235 18.0043 16.9335 16.0643L17.6335 10.4743C17.7435 9.48427 17.7635 8.04427 16.8035 6.96427ZM8.5035 1.41427C8.98813 1.36559 9.47758 1.41913 9.94023 1.57143C10.4029 1.72372 10.8284 1.97138 11.1894 2.29841C11.5503 2.62544 11.8387 3.02456 12.0357 3.46998C12.2328 3.91539 12.3343 4.39721 12.3335 4.88427V5.58427H5.3535V5.06427C5.3535 3.28427 6.8235 1.57427 8.5035 1.41427ZM5.2635 11.1543H5.2535C4.7035 11.1543 4.2535 10.7043 4.2535 10.1543C4.2535 9.60427 4.7035 9.15427 5.2535 9.15427C5.8135 9.15427 6.2635 9.60427 6.2635 10.1543C6.2635 10.7043 5.8135 11.1543 5.2635 11.1543ZM12.2635 11.1543H12.2535C11.7035 11.1543 11.2535 10.7043 11.2535 10.1543C11.2535 9.60427 11.7035 9.15427 12.2535 9.15427C12.8135 9.15427 13.2635 9.60427 13.2635 10.1543C13.2635 10.7043 12.8135 11.1543 12.2635 11.1543Z" />
    </svg>
);

export interface GridSquareCardProps {
    displayName: string;
    username: string;
    avatarUrl?: string;
    price?: string | number;
    bg?: string;
    textColor?: string;
    grainOpacity?: number;
    sectionId?: string;
    itemType?: string;
    isAuction?: boolean;
    isOffer?: boolean;
    isSold?: boolean;
    isBrand?: boolean;
    initialSeconds?: number;
    hasBids?: boolean;
    bidsCount?: number;
    userKind?: UserKind;
    compact?: boolean;
    onClick?: () => void;
    onOffer?: () => void;
    [key: string]: any;
}

export const GridSquareCard: React.FC<GridSquareCardProps> = (props) => {
    const {
        displayName,
        username,
        avatarUrl = '/icons/default-avatar.svg',
        price = '1,000',
        bg,
        textColor,
        grainOpacity = 0.30,
        itemType,
        isAuction,
        isOffer,
        isSold,
        isBrand,
        initialSeconds = 0,
        hasBids = false,
        bidsCount = 0,
        userKind: userKindProp,
        compact = false,
        onClick,
        onOffer,
    } = props;

    const { t } = useTranslation('store');

    const userKind = (userKindProp && userKindProp !== 'p2p') ? userKindProp : getUserKind(props);
    let selected = DARK_PALETTES.find(p => p.bg.toLowerCase() === String(bg).toLowerCase());
    if (!selected) {
        let hash = 0;
        const key = username + displayName;
        for (let i = 0; i < key.length; i++) hash += key.charCodeAt(i);
        selected = DARK_PALETTES[Math.abs(hash) % DARK_PALETTES.length];
    }
    const pal = selected ?? DARK_PALETTES[1];
    const cardColor = textColor || pal.textColor;

    const [seconds, setSeconds] = useState(initialSeconds);
    const [copied, setCopied] = useState(false);
    
    useEffect(() => {
        setSeconds(initialSeconds);
    }, [initialSeconds]);

    useEffect(() => {
        if (!isAuction || !hasBids || seconds <= 0) return;
        const interval = setInterval(() => {
            setSeconds(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, [isAuction, hasBids, seconds]);

    const formatTime = (s: number) => {
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        return `${d}${t('daysUnit')} ${h}${t('hoursUnit')} ${m}${t('minutesUnit')}`;
    };

    const copyUsername = (e: React.MouseEvent) => {
        e.stopPropagation();
        const text = `@${String(username || '').replace(/^@/, '').toLowerCase()}`;
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(() => {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.cssText = 'position:fixed;opacity:0';
                document.body.appendChild(ta);
                ta.select();
                try { document.execCommand('copy'); done(); } catch {}
                document.body.removeChild(ta);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.cssText = 'position:fixed;opacity:0';
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); done(); } catch {}
            document.body.removeChild(ta);
        }
    };

    const handleArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const cleanUser = String(username || '').replace(/^@/, '').trim();
        if (!cleanUser) return;
        const url = `https://t.me/${cleanUser}`;
        // @ts-ignore
        if (window.Telegram?.WebApp?.openTelegramLink) {
            // @ts-ignore
            window.Telegram.WebApp.openTelegramLink(url);
        } else {
            window.open(url, '_blank');
        }
    };

    const cs = (full: string, small: string) => (compact ? small : full);

    const typeBadge = isBrand
        ? { label: t('common:brands', 'Brand'), cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' }
        : isSold
            ? { label: t('common:sold', 'SOLD'), cls: 'bg-red-500/15 text-red-400 border-red-500/30' }
            : isAuction
                ? { label: t('common:auctions', 'AUCTION'), cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' }
                : itemType === 'STORE'
                    ? { label: t('common:store', 'STORE'), cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' }
                    : { label: t('common:live', 'LIVE'), cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };

    return (
        <div
            onClick={onClick}
            className={`glass relative cursor-pointer select-none w-full aspect-[5/4] overflow-hidden border border-white/10 active:scale-[0.97] transition-transform transform-gpu flex flex-col ${cs('rounded-[20px] p-2.5', 'rounded-[14px] p-[7px]')}`}
            style={{ color: cardColor, ['--grain-opacity' as any]: grainOpacity }}
        >
            {/* Grain */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ opacity: 0.30, mixBlendMode: 'overlay', backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '4px 4px' }}
            />

            {/* Top row: Avatar + Name on Left, Icons on Right */}
            <div className="flex items-start justify-between relative z-10 w-full">
                {/* Seller Info (Avatar + displayName) */}
                <div className={`flex items-center shrink-0 max-w-[50%] ${cs('gap-1.5', 'gap-[5px]')}`}>
                    <div className={`rounded-full border-[1px] border-white/20 glass-chip flex items-center justify-center shrink-0 ${cs('w-6 h-6 p-[1.5px]', 'w-[18px] h-[18px] p-[1px]')}`}>
                        <img
                            src={avatarUrl}
                            alt={displayName}
                            className="w-full h-full object-cover rounded-full"
                            onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                        />
                    </div>
                    <span className={`${cs('text-[10px]', 'text-[8.5px]')} font-bold truncate`} style={{ color: cardColor }}>
                        {displayName}
                    </span>
                </div>
                
                {/* Action Icons (UserType, Arrow - Top Right) */}
                <div className={`flex items-center shrink-0 ${cs('gap-1', 'gap-[3px]')}`}>
                    <button type="button" className={`glass-chip rounded flex items-center justify-center hover:bg-white/20 transition-colors ${cs('w-6 h-6', 'w-[18px] h-[18px]')}`}>
                        <UserTypeIcon kind={userKind} className={cs('w-3.5 h-3.5', 'w-2.5 h-2.5') + ' text-white'} />
                    </button>
                    <button
                        type="button"
                        onClick={handleArrowClick}
                        aria-label="open-telegram"
                        title={username ? `https://t.me/${username.replace(/^@/, '')}` : t('openTelegram')}
                        className={`relative flex items-center justify-center rounded glass-chip hover:bg-white/20 transition-colors ${cs('w-6 h-6', 'w-[18px] h-[18px]')}`}
                    >
                        <ArrowUpRight className={cs('w-3.5 h-3.5', 'w-2.5 h-2.5') + ' text-white stroke-[2.5]'} />
                    </button>
                </div>
            </div>

            {/* Middle: The Username being sold */}
            <div className={`flex flex-col items-center justify-center relative z-10 w-full flex-1 ${cs('gap-1', 'gap-[3px]')}`}>

                <div className={`relative flex items-center justify-center gap-0.5 font-extrabold ${cs('text-base', 'text-[13px]')}`} style={{ color: cardColor }}>
                    <div className={`rounded-full border border-white/10 flex items-center justify-center shrink-0 glass-chip ${cs('w-7 h-7', 'w-[22px] h-[22px]')}`}>
                        <AtSign className={cs('w-3.5 h-3.5', 'w-2.5 h-2.5') + ' stroke-[2.2]'} style={{ color: cardColor }} />
                    </div>
                    <span className={`font-mono truncate ${cs('ml-1', 'ml-[3px]')}`}>{username ? username.toLowerCase() : ''}</span>
                    {username ? (
                        <button
                            type="button"
                            onClick={copyUsername}
                            title={t('copyAt', { name: username.toLowerCase() })}
                            aria-label={t('copyUsername')}
                            className="absolute -top-2 -right-1 opacity-60 hover:opacity-100 transition-opacity shrink-0"
                        >
                            {copied ? (
                                <Check className="w-[11px] h-[11px] text-emerald-300" />
                            ) : (
                                <Copy className="w-[11px] h-[11px]" />
                            )}
                        </button>
                    ) : null}
                </div>

                {/* Offer cards only: small smooth text "top offer : <amount>" + GramIcon above Make Offer CTA (Only if offer > 0 exists) */}
                {isOffer && !isAuction && (() => {
                    const rawVal = props.highestOffer ?? props.highest_offer ?? props.top_offer ?? props.currentOffer ?? props.topOfferAmt ?? props.itemData?.highestOffer ?? props.itemData?.highest_offer ?? props.itemData?.top_offer ?? props.itemData?.currentOffer;
                    const numVal = Number(rawVal);
                    if (!rawVal || isNaN(numVal) || numVal <= 0) return null;
                    return (
                        <div className="flex items-center justify-center gap-1 mb-1 font-mono text-[9.5px] font-bold text-purple-300/90 tracking-tight">
                            <span>{t('topOffer', { amount: numVal.toLocaleString() })}</span>
                            <GramIcon className="w-3 h-3 text-purple-300" />
                        </div>
                    );
                })()}

                {isAuction && (
                    <div className="text-[8.5px] font-bold opacity-80 uppercase tracking-wide bg-black/20 px-2 py-0.5 rounded-full border border-white/5">
                        {hasBids ? formatTime(seconds) : t('placeBidAndStart')}
                    </div>
                )}
            </div>

            {/* Bottom: Price Bar / Make Offer CTA */}
            <div className={`flex items-center justify-center relative z-10 mt-auto glass border border-white/10 w-full ${cs('rounded-xl p-2', 'rounded-[9px] p-[7px]')}`}>
                {isOffer && !isAuction ? (
                    <div className={`flex items-center justify-center ${cs('gap-1.5', 'gap-1')}`}>
                        <Handshake className={cs('w-4 h-4', 'w-3 h-3') + ' text-purple-300'} />
                        <span className={`${cs('text-[10px]', 'text-[9px]')} font-extrabold uppercase tracking-widest text-purple-300`}>{t('makeOffer')}</span>
                    </div>
                ) : (
                    <>
                        {onOffer && isOffer && !isAuction && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOffer(); }}
                                title={t('makeOffer')}
                                className="absolute -top-7 right-1.5 flex items-center gap-1 pl-1.5 pr-2 py-1 rounded-lg border border-purple-400/30 bg-purple-500/15 hover:bg-purple-500/30 backdrop-blur-sm transition-colors active:scale-95"
                            >
                                <Handshake className="w-3 h-3 text-purple-300" />
                                <span className="text-[8px] font-extrabold uppercase tracking-widest text-purple-300">{t('offerCta')}</span>
                            </button>
                        )}
                        <div className={`flex items-center ${cs('gap-1.5', 'gap-1')}`}>
                            <span className={`${cs('text-[14px]', 'text-[12px]')} font-extrabold font-mono ${isAuction ? 'text-orange-400' : ''}`} style={!isAuction ? { color: cardColor } : undefined}>
                                {price}
                            </span>
                            <GramIcon className={`${cs('w-4 h-4', 'w-3 h-3')} ${isAuction ? 'text-orange-400' : ''}`} />
                        </div>
                        <button type="button" className={`absolute right-1.5 rounded-lg flex items-center justify-center transition-colors ${cs('w-7 h-7', 'w-[22px] h-[22px]')} ${isAuction ? 'bg-orange-500/20 border border-orange-400/30 hover:bg-orange-500/35' : 'glass-chip hover:bg-white/20'}`}>
                            {isAuction ? (
                                <>
                                    <Gavel className={`${cs('w-4 h-4', 'w-3 h-3')} ${hasBids ? 'text-amber-300' : 'text-white'}`} />
                                    {hasBids && bidsCount > 0 && (
                                        <span className="absolute -top-1 -right-1 glass-chip !bg-red-500/40 text-white text-[7px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center border border-red-400/50 shadow-[0_2px_4px_rgba(239,68,68,0.2)] leading-none">
                                            {bidsCount > 99 ? '99+' : bidsCount}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <NavStoreIcon className={cs('w-4 h-4', 'w-3 h-3') + ' text-white'} />
                            )}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default GridSquareCard;
